import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GuaranteeTransaction } from './guarantee-types';
import { formatCurrency, formatDateTime } from '../utils';

interface VoidTransactionModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	transaction: GuaranteeTransaction;
	onConfirm: (transaction: GuaranteeTransaction, notes?: string) => Promise<void>;
	isSubmitting: boolean;
}

export const VoidTransactionModal = ({
	open,
	onOpenChange,
	transaction,
	onConfirm,
	isSubmitting,
}: VoidTransactionModalProps) => {
	const { t: tC } = useTranslation('contracts');
	const [notes, setNotes] = useState('');

	const handleSubmit = async () => {
		await onConfirm(transaction, notes || undefined);
		setNotes('');
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			setNotes('');
		}
		onOpenChange(nextOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent size="sm">
				<DialogHeader>
					<DialogTitle>{tC('guarantees.voidTransactionTitle')}</DialogTitle>
					<DialogDescription>
						{tC('guarantees.voidTransactionDescription')}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					{/* Transaction summary */}
					<div className="rounded-md bg-muted/50 p-3 space-y-1 text-sm">
						<div className="flex justify-between">
							<span className="text-muted-foreground">
								{tC('guarantees.type')}
							</span>
							<span className="font-medium">
								{transaction.guaranteeTransactionType.name}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-muted-foreground">
								{tC('guarantees.amount')}
							</span>
							<span className="font-medium">
								{formatCurrency(transaction.grossAmount)}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="text-muted-foreground">
								{tC('guarantees.date')}
							</span>
							<span className="font-medium">
								{formatDateTime(transaction.createdAt)}
							</span>
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="void-notes">
							{tC('guarantees.notes')}
						</Label>
						<Textarea
							id="void-notes"
							placeholder={tC('guarantees.voidNotesPlaceholder')}
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							rows={2}
							autoFocus
						/>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => handleOpenChange(false)}
						disabled={isSubmitting}
					>
						{tC('guarantees.cancel')}
					</Button>
					<Button
						variant="destructive"
						onClick={handleSubmit}
						disabled={isSubmitting}
					>
						{isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
						{tC('guarantees.confirmVoid')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
